/*
 * GA4 rejects oversized hits, so impressions are split into batches of 10.
 * Indices are re-assigned from a per-list counter (with dedup) rather than
 * trusting product.position, as insurance against reports of it skipping
 */
window.__viewItemListState = window.__viewItemListState || {};

/**
 * Maps a raw Elevar product/impression array to a GA4 ecommerce event
 *
 * @param {object} item - Raw event object passed to window.ElevarTransformFn
 * @param {string} eventName - GA4 event name (e.g. "view_item_list", "add_to_cart")
 * @param {object[]} products - Raw product/impression objects for this event
 * @returns {object} The event to push to the dataLayer
 */
function buildEcommerceEvent(item, eventName, products) {

  /* Match product.list against data-list-id so mixed batches (e.g. a grid
     plus a recommendations module) don't all get the first container's
     name/source_module */
  var listContainers = (eventName === 'view_item_list')
    ? document.querySelectorAll('[data-list-id]')
    : null;

  /* product object has id, name, brand,
     category, variant, price, position, list, product_id, variant_id,
     compare_at_price, image
     Cart/add product arrays are assumed to match, plus quantity */
  var transformedItems = products.map(function(product) {

    /* window.dkProductMetafields is populated per-page in sections/collection--body.liquid and sections/product.liquid */
    var productMeta = window.dkProductMetafields &&
      window.dkProductMetafields[String(product.product_id)];

    var listContainer = (eventName === 'view_item_list')
      ? findListContainer(listContainers, product.list)
      : document.querySelector('[data-list-id]');

    var currentListId = listContainer
      ? listContainer.getAttribute('data-list-id')
      : null;

    var currentListName = listContainer
      ? listContainer.getAttribute('data-list-name')
      : null;

    var currentSourceModule = listContainer
      ? listContainer.getAttribute('data-source-module')
      : null;

    var transformedItem = {
      item_id: product.id,
      item_name: product.name,
      item_brand: product.brand,
      item_category: productMeta
        ? productMeta.department
        : product.category,
      item_category2: productMeta
        ? productMeta.class
        : undefined,
      item_category3: productMeta
        ? productMeta.subclass
        : undefined,
      item_variant: product.variant,
      price: Number(product.price),
      item_list_name:
        (eventName === 'view_item_list')
          ? (currentListName || product.list)
          : (sessionStorage.getItem('ga4_item_list_name') || product.list),

      item_list_id:
        (eventName === 'view_item_list')
          ? (currentListId || product.list)
          : (sessionStorage.getItem('ga4_item_list_id') || product.list),

      source_module:
        (eventName === 'view_item_list')
          ? (currentSourceModule || undefined)
          : (sessionStorage.getItem('ga4_source_module') || undefined),

      item_product_id: product.product_id,
      item_variant_id: product.variant_id
    };

    if (product.quantity != null) {
      transformedItem.quantity = Number(product.quantity);
    }

    if (product.position != null) {
      transformedItem.index = Number(product.position);
    }

    return transformedItem;
  });

  if (eventName === 'view_item_list') {
    return buildViewItemListEvent(item, transformedItems);
  }

  var currency = item.ecommerce.currencyCode;
  var value = 0;

  transformedItems.forEach(function(product) {
    value += product.price * (product.quantity || 1);
  });

  return {
    ...item,
    event: eventName,
    ecommerce: {
      currency: currency,
      value: value,
      items: transformedItems
    }
  };
}

/**
 * Finds the [data-list-id] element matching listId, falling back to the first container
 *
 * @param {NodeList|Element[]|null} containers - Candidate list containers
 * @param {string|undefined} listId - Elevar's product.list value to match against data-list-id
 * @returns {Element|null} The matching container, the first container, or null
 */
function findListContainer(containers, listId) {
  if (!containers) return null;

  for (var i = 0; i < containers.length; i++) {
    if (listId && containers[i].getAttribute('data-list-id') === listId) {
      return containers[i];
    }
  }

  return containers[0] || null;
}

/**
 * Gets (creating if needed) the persistent index/dedup state for one list_id
 *
 * @param {string|undefined} listId - item_list_id to key the state by ("default" if omitted)
 * @returns {{nextIndex: number, seen: Set<string>}} The list's running index counter and seen-item keys
 */
function getViewItemListState(listId) {
  var key = listId || 'default';

  if (!window.__viewItemListState[key]) {
    window.__viewItemListState[key] = {
      nextIndex: 0,
      seen: new Set()
    };
  }

  return window.__viewItemListState[key];
}

/**
 * Splits transformed view_item_list items into per-list batches of 10, deduping
 * against previously-seen items and re-assigning contiguous indices. Extra
 * batches are pushed to window.dataLayer directly; the first is returned for
 * window.ElevarTransformFn's caller to push
 *
 * @param {object} item - Raw event object (spread onto each batch's returned event)
 * @param {object[]} items - GA4 items from buildEcommerceEvent, one per impression
 * @returns {object} The first batch's view_item_list event, or item unchanged if nothing was new
 */
function buildViewItemListEvent(item, items) {
  /* One call can mix items from more than one list, so index/dedup/batches are tracked per list_id */
  var byList = {};
  var listOrder = [];

  items.forEach(function(product) {
    var listKey = product.item_list_id || 'default';

    if (!byList[listKey]) {
      byList[listKey] = [];
      listOrder.push(listKey);
    }

    byList[listKey].push(product);
  });

  var batches = [];

  listOrder.forEach(function(listKey) {
    var state = getViewItemListState(listKey);
    var newItems = [];

    byList[listKey].forEach(function(product) {
      var key = product.item_product_id + ':' + product.item_variant_id;

      if (state.seen.has(key)) return;

      state.seen.add(key);
      product.index = state.nextIndex++;
      newItems.push(product);
    });

    for (var i = 0; i < newItems.length; i += 10) {
      batches.push(newItems.slice(i, i + 10));
    }
  });

  /* Nothing new to report: return the event unmodified so it isn't sent */
  if (!batches.length) {
    return item;
  }

  var currency = item.ecommerce.currencyCode;

  /**
   * @param {object[]} batch - Up to 10 items for one view_item_list hit
   * @returns {object} A view_item_list event for this batch
   */
  function toGa4Event(batch) {
    var value = 0;

    batch.forEach(function(product) {
      value += product.price * (product.quantity || 1);
    });

    return {
      ...item,
      event: 'view_item_list',
      ecommerce: {
        currency: currency,
        value: value,
        items: batch
      }
    };
  }

  window.dataLayer = window.dataLayer || [];

  for (var b = 1; b < batches.length; b++) {
    window.dataLayer.push(toGa4Event(batches[b]));
  }

  return toGa4Event(batches[0]);
}
